/**
 * <UpgradeToPortfolioModal /> — the "Upgrade to Portfolio Clarity" modal.
 *
 * Live-reads the 4 qualifying criteria from /api/upgrade/qualifying-criteria
 * and adapts its bottom CTA:
 *   - All 4 met → "Enroll in Portfolio Clarity" (dispatches
 *     openSubscriptionModal with targetTier=premium_portfolio, reusing the
 *     existing subscription/payment plumbing).
 *   - Any missing → "Request Invitation" (opens existing AccessRequestModal
 *     so the admin-review flow is preserved).
 *
 * Rendered exactly once from App.js — driven by UpgradeModalContext.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, X, Sparkles, ArrowRight } from 'lucide-react';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import { useQualifyingCriteria } from '../../hooks/useQualifyingCriteria';
import { useUserTier } from '../../hooks/useUserTier';
import AccessRequestModal from '../AccessRequestModal';

const CRITERIA_LABELS = [
  { key: 'continuity_created', label: 'Set up your Continuity System system',
    fixLabel: 'Go to Continuity System', targetTab: 'estate-vault' },
  { key: 'asset_value_met', label: 'Record assets worth $500,000 or more',
    fixLabel: 'Add investments', targetTab: 'add' },
  { key: 'contacts_and_docs_met', label: 'Add trusted contacts and key documents',
    fixLabel: 'Go to Continuity System', targetTab: 'estate-vault' },
  { key: 'completeness_met', label: 'Reach 75% portfolio completeness',
    fixLabel: 'Complete investment details', targetTab: 'investments' },
];

function CriterionRow({ met, label, hint, fixLabel, onFixClick }) {
  const Icon = met ? Check : X;
  const iconCls = met ? 'text-emerald-600' : 'text-slate-400';
  const textCls = met ? 'text-slate-900' : 'text-slate-600';
  return (
    <li className="flex items-start gap-3 py-2" data-testid={`criterion-${met ? 'met' : 'unmet'}`}>
      <span className={`shrink-0 mt-0.5 rounded-full p-1 ${met ? 'bg-emerald-50' : 'bg-slate-100'}`}>
        <Icon className={`w-3.5 h-3.5 ${iconCls}`} strokeWidth={3} />
      </span>
      <span className="flex-1">
        <span className={`block text-sm font-medium ${textCls}`}>{label}</span>
        {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
        {!met && fixLabel && (
          <button
            type="button"
            onClick={onFixClick}
            className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline font-medium"
            data-testid={`criterion-fix-${fixLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            {fixLabel} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </span>
    </li>
  );
}

function _hint(key, details) {
  if (!details) return null;
  if (key === 'asset_value_met') {
    const cur = Math.round(details.assets_recorded_usd || 0).toLocaleString();
    return `Currently recorded: $${cur}`;
  }
  if (key === 'completeness_met') {
    return `Currently: ${details.completeness_percent ?? 0}%`;
  }
  return null;
}

const UpgradeToPortfolioModal = () => {
  const { isOpen, closeUpgradeModal, sourceFeature } = useUpgradeModal();
  const { data, loading } = useQualifyingCriteria(isOpen);
  // Re-fetch "who am I?" every time the modal opens so the AccessRequestModal
  // downstream prefill uses fresh, post-login data (see hooks/useUserTier.js).
  const [userFetchKey, setUserFetchKey] = useState(0);
  useEffect(() => {
    if (isOpen) setUserFetchKey((k) => k + 1);
  }, [isOpen]);
  const { user } = useUserTier(userFetchKey);
  const [showAccessRequest, setShowAccessRequest] = useState(false);

  const qualifies = !!data?.qualifies_for_premium;
  const criteria = data?.criteria || {};
  const details = data?.details || {};

  const handleEnroll = () => {
    window.dispatchEvent(new CustomEvent('openSubscriptionModal', {
      detail: { defaultType: 'monthly', targetTier: 'premium_portfolio' },
    }));
    closeUpgradeModal();
  };

  const handleRequestInvitation = () => {
    setShowAccessRequest(true);
    closeUpgradeModal();
  };

  const handleFixClick = (targetTab) => {
    // Canonical cross-tab jump event (see useCrossTabJump.js). Consolidated
    // from the legacy 'setDashboardTab' event in Session 260 — one event,
    // bare string detail, one listener.
    window.dispatchEvent(new CustomEvent('mif:go-to-tab', { detail: targetTab }));
    closeUpgradeModal();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(o) => { if (!o) closeUpgradeModal(); }}>
        <DialogContent className="max-w-md" data-testid="upgrade-to-portfolio-modal">
          <DialogTitle className="sr-only">Upgrade to Portfolio Clarity</DialogTitle>
          <div className="flex items-start gap-3 pb-1">
            <div className="shrink-0 bg-emerald-50 border border-emerald-100 rounded-lg p-2">
              <Sparkles className="w-5 h-5 text-emerald-700" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-serif font-semibold text-slate-900">
                Upgrade to Portfolio Clarity
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                {sourceFeature
                  ? `"${sourceFeature}" is available on Portfolio Clarity.`
                  : 'Unlock allocation, performance, tax pack, insights, net worth and sharing.'}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-6 text-center text-sm text-slate-500">Checking your status…</div>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100 border rounded-md px-3 bg-slate-50/50">
              {CRITERIA_LABELS.map(({ key, label, fixLabel, targetTab }) => (
                <CriterionRow
                  key={key}
                  met={!!criteria[key]}
                  label={label}
                  hint={_hint(key, details)}
                  fixLabel={fixLabel}
                  onFixClick={() => handleFixClick(targetTab)}
                />
              ))}
            </ul>
          )}

          <div className="mt-5 flex flex-col gap-2">
            {qualifies ? (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleEnroll}
                data-testid="upgrade-enroll-btn"
              >
                Enroll in Portfolio Clarity
              </Button>
            ) : (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleRequestInvitation}
                data-testid="upgrade-request-invitation-btn"
                disabled={loading}
              >
                Request Invitation
              </Button>
            )}
            <a
              href="/pricing-layers"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-center text-emerald-700 hover:text-emerald-800 underline underline-offset-2 mt-1"
              data-testid="upgrade-see-comparison-link"
            >
              See full comparison →
            </a>
            <button
              type="button"
              onClick={closeUpgradeModal}
              className="text-xs text-slate-500 hover:text-slate-700 mt-1"
              data-testid="upgrade-modal-close-btn"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AccessRequestModal
        open={showAccessRequest}
        onClose={() => setShowAccessRequest(false)}
        introductionData={null}
        prefillToken={null}
        prefillFromUser={user}
      />
    </>
  );
};

export default UpgradeToPortfolioModal;
